import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../App";
import { getSamples, submitAlphaFeedback } from "../../services/api";
import ReportSelector, { ReportHistorySelector } from "./ReportSelector";
import VehicleCorrectionActions from "./VehicleCorrectionActions";
import SampleSummary from "./SampleSummary";
import VehicleTrendHero, { OilGeekNote } from "./VehicleTrendHero";
import GaugeGrid, { clearSampleDetailCache } from "../Gauges/GaugeGrid";
import ReportReviewModal from "../Admin/ReportReviewModal";
import useIsMobile from "../../hooks/useIsMobile";
import { groupSamplesByVehicle } from "../../services/vehicleGrouping";

const s = {
  page: { maxWidth: "var(--dashboard-max-width)", margin: "0 auto" },
  approvalNotice: { position: "fixed", top: 78, right: 20, zIndex: 1600, width: "min(440px, calc(100vw - 40px))", padding: "14px 16px", border: "1px solid #86efac", borderRadius: 8, background: "#f0fdf4", color: "#166534", boxShadow: "0 16px 40px rgba(15,23,42,.18)", fontSize: 13, fontWeight: 800, lineHeight: 1.45 },
  accountRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 4 },
  welcome: { fontSize: 14, color: "var(--text-muted)" },
  infoButton: {
    border: "1.5px solid var(--border)", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)",
    padding: "5px 10px", fontSize: 12, fontWeight: 800, cursor: "pointer",
  },
  title: {
    fontFamily: "var(--font-heading)",
    fontSize: "var(--dashboard-title-size)",
    fontWeight: "var(--dashboard-title-weight)",
    color: "var(--text-primary)",
    marginBottom: 24,
  },

  loadingBox: {
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
    minHeight: 360, gap: 16,
  },
  loadingCard: {
    width: "min(520px, 100%)",
    background: "var(--bg-card)",
    border: "1px solid var(--border)",
    borderRadius: "var(--dashboard-card-radius)",
    boxShadow: "var(--dashboard-card-shadow)",
    padding: "26px 24px",
    textAlign: "center",
  },
  spinner: {
    width: 34, height: 34, border: "3px solid var(--border)", borderTopColor: "var(--teal)",
    borderRadius: "50%", animation: "spin 0.8s linear infinite",
  },
  loadingTitle: { fontSize: 18, fontWeight: 900, color: "var(--text-primary)", marginTop: 14 },
  loadingText: { fontSize: 14, color: "var(--text-muted)", marginTop: 6, lineHeight: 1.5 },
  loadingTrack: {
    position: "relative",
    height: 10,
    overflow: "hidden",
    borderRadius: 999,
    background: "var(--border-subtle)",
    border: "1px solid var(--border)",
    marginTop: 18,
  },
  loadingFill: {
    position: "absolute",
    inset: "0 auto 0 0",
    width: "46%",
    borderRadius: 999,
    background: "linear-gradient(90deg, color-mix(in srgb, var(--teal) 38%, transparent), var(--teal), color-mix(in srgb, var(--teal) 38%, transparent))",
  },
  loadingMeta: { fontSize: 12, color: "var(--text-faint)", marginTop: 10, fontWeight: 700 },

  pendingCard: {
    background: "var(--bg-card)", borderRadius: "var(--dashboard-card-radius)", padding: "60px 40px", textAlign: "center",
    boxShadow: "var(--dashboard-card-shadow)", maxWidth: 500, margin: "60px auto",
    border: "1px solid var(--border)",
  },
  pendingIcon: { fontSize: 48, marginBottom: 16 },
  pendingTitle: { fontSize: 20, fontWeight: 700, color: "var(--text-primary)", marginBottom: 8 },
  pendingText: { fontSize: 15, color: "var(--text-muted)", lineHeight: 1.6 },

  errorCard: {
    background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
    padding: "20px 24px", textAlign: "center", maxWidth: 500, margin: "60px auto",
  },
  errorText: { fontSize: 14, color: "#dc2626" },
  retryBtn: {
    marginTop: 12, padding: "8px 20px", background: "#dc2626", color: "#fff",
    border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.58)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 18,
  },
  modal: {
    width: "min(980px, 96vw)", maxHeight: "88vh", overflow: "auto",
    background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 10,
    boxShadow: "0 24px 80px rgba(15, 23, 42, 0.35)", padding: 18,
  },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 12 },
  modalTitle: { fontSize: 18, fontWeight: 900, color: "var(--text-primary)" },
  modalHint: { fontSize: 12, color: "var(--text-muted)", marginTop: 4 },
  modalClose: {
    border: "1.5px solid var(--border)", borderRadius: "var(--dashboard-control-radius)",
    background: "var(--dashboard-selector-bg)", color: "var(--text-primary)",
    padding: "8px 12px", fontSize: 13, fontWeight: 800, cursor: "pointer",
  },
  infoGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 },
  infoCard: { border: "1px solid var(--border)", borderRadius: 8, background: "var(--bg-card-subtle)", padding: 12 },
  infoLabel: { fontSize: 11, fontWeight: 800, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" },
  infoValue: { fontSize: 14, color: "var(--text-primary)", marginTop: 5, wordBreak: "break-word", lineHeight: 1.4 },
  adminTemplateCard: {
    background: "color-mix(in srgb, var(--teal) 10%, transparent)",
    border: "1px solid var(--teal)",
    borderRadius: "var(--dashboard-card-radius)",
    boxShadow: "var(--dashboard-card-shadow)",
    color: "var(--text-primary)",
    margin: "-8px 0 24px",
    padding: "14px 18px",
  },
  adminTemplateLabel: { fontSize: 11, fontWeight: 900, color: "var(--teal-dark)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 5 },
  adminTemplateValue: { fontSize: 17, fontWeight: 900, color: "var(--text-primary)" },
  adminTemplateNote: { fontSize: 12, fontWeight: 700, color: "var(--text-muted)", marginTop: 4 },
};

function firstValue(samples, keys) {
  for (const sample of samples || []) {
    for (const key of keys) {
      const value = sample?.[key];
      if (value !== null && value !== undefined && String(value).trim() !== "") return String(value).trim();
    }
  }
  return "";
}

function buildCustomerInfo({ samples, user, adminViewEmail }) {
  const addressParts = [
    firstValue(samples, ["customer_address_line_1"]),
    firstValue(samples, ["customer_address_line_2"]),
    [
      firstValue(samples, ["customer_city"]),
      firstValue(samples, ["customer_state"]),
      firstValue(samples, ["customer_postal_code"]),
    ].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " $1"),
    firstValue(samples, ["customer_country"]),
  ].filter(Boolean);

  return {
    name: firstValue(samples, ["customer_name"]) || user?.customerName || "",
    email: adminViewEmail || firstValue(samples, ["customer_email"]) || user?.email || "",
    phone: firstValue(samples, ["customer_phone_number"]),
    address: firstValue(samples, ["customer_address"]) || addressParts.join("\n"),
  };
}

function AccountHeader({ customerInfo, user, adminViewEmail, onOpenInfo }) {
  return (
    <>
      <div style={s.accountRow}>
        <div style={s.welcome}>
          {adminViewEmail
            ? `Admin view: ${customerInfo.email}`
            : `Account: ${customerInfo.email || user?.email || "Unknown email"}`}
        </div>
        <button type="button" style={s.infoButton} onClick={onOpenInfo}>My Info</button>
      </div>
      <h1 style={s.title}>Your Oil Analysis Reports</h1>
    </>
  );
}

function AdminThresholdTemplateSection({ templateName, thresholdMode, onOpenReview }) {
  if (!templateName && !onOpenReview) return null;
  return (
    <section style={s.adminTemplateCard}>
      <div style={s.adminTemplateLabel}>Admin Threshold Template</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={s.adminTemplateValue}>{templateName || "Not resolved"}</div>
          <div style={s.adminTemplateNote}>
            {thresholdMode === "custom" ? "Custom thresholds are saved for this report." : "This report is using global thresholds."}
          </div>
        </div>
        <button type="button" style={s.modalClose} onClick={onOpenReview}>Thresholds and Notes</button>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const isAdminRole = user?.role === "admin" || user?.role === "collaborator";
  const isAlphaTester = user?.role === "customer" && Boolean(user?.isAlphaTester);
  const adminViewEmail = isAdminRole ? searchParams.get("adminEmail") || "" : "";
  const adminReportId = isAdminRole ? searchParams.get("sampleId") || "" : "";
  const reviewMode = isAdminRole && searchParams.get("review") === "1";
  const adminDeepLinkKey = `${adminViewEmail}|${adminReportId}`;
  const [samples, setSamples] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [activeGaugeTab, setActiveGaugeTab] = useState("contamination");
  const [selectedTemplateName, setSelectedTemplateName] = useState("");
  const [vehicleDetailsExpanded, setVehicleDetailsExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMyInfo, setShowMyInfo] = useState(false);
  const [showAlphaFeedback, setShowAlphaFeedback] = useState(false);
  const [showReportReview, setShowReportReview] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [approvalNotice, setApprovalNotice] = useState("");
  const [reportLearningPreview, setReportLearningPreview] = useState(null);
  const [reviewMetricRequest, setReviewMetricRequest] = useState(null);
  const [feedbackDraftReportIds, setFeedbackDraftReportIds] = useState(() => new Set());
  const feedbackDraftMemoryRef = useRef({ drafts: new Map(), urls: new Set() });
  const [appliedAdminDeepLinkKey, setAppliedAdminDeepLinkKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSamples = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSamples(adminViewEmail);
      const data = res.data.samples || [];
      setSamples(data);
    } catch (err) {
      setError("Failed to load your samples. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    feedbackDraftMemoryRef.current.urls.forEach((url) => URL.revokeObjectURL(url));
    feedbackDraftMemoryRef.current.urls.clear();
    feedbackDraftMemoryRef.current.drafts.clear();
    setFeedbackDraftReportIds(new Set());
    setSelectedVehicleId(null);
    setSelectedId(null);
    setSelectedTemplateName("");
    setSelectedDetail(null);
    setReportLearningPreview(null);
    setReviewMetricRequest(null);
    setAppliedAdminDeepLinkKey("");
    fetchSamples();
  }, [adminViewEmail]);

  useEffect(() => () => {
    feedbackDraftMemoryRef.current.urls.forEach((url) => URL.revokeObjectURL(url));
    feedbackDraftMemoryRef.current.urls.clear();
    feedbackDraftMemoryRef.current.drafts.clear();
  }, []);

  const handleFeedbackDraftStateChange = useCallback((sampleId, hasDraft) => {
    setFeedbackDraftReportIds((current) => {
      if (current.has(sampleId) === hasDraft) return current;
      const next = new Set(current);
      if (hasDraft) next.add(sampleId);
      else next.delete(sampleId);
      return next;
    });
  }, []);

  const handleTemplateResolved = useCallback((templateName) => {
    setSelectedTemplateName(templateName || "");
  }, []);

  const handleDetailLoaded = useCallback((detail) => {
    setSelectedDetail(detail);
  }, []);

  useEffect(() => {
    setAppliedAdminDeepLinkKey("");
  }, [adminDeepLinkKey]);

  useEffect(() => {
    setVehicleDetailsExpanded(false);
  }, [selectedVehicleId]);

  const vehicleGroups = useMemo(() => groupSamplesByVehicle(samples), [samples]);
  const readySamples = samples.filter(s => s.has_test_measurements?.toUpperCase() === "YES");
  const allPending = samples.length > 0 && readySamples.length === 0;
  const selectedGroup = vehicleGroups.find(group => group.id === selectedVehicleId) || null;
  const selectedSample = selectedGroup?.readySamples.find(s => s.lab_sample_id === selectedId) || null;
  const currentSelectedDetail = selectedDetail?.sample?.lab_sample_id === selectedId ? selectedDetail : null;
  const activeReportLearningPreview = reportLearningPreview?.sampleId === selectedId ? reportLearningPreview : null;
  const handleReportLearningPreview = useCallback((preview) => {
    setReportLearningPreview(preview ? { ...preview, sampleId: selectedId } : null);
  }, [selectedId]);
  const refreshSelectedDetail = useCallback(() => {
    clearSampleDetailCache(selectedSample?.lab_sample_id, adminViewEmail);
    setDetailRefreshKey((value) => value + 1);
  }, [selectedSample?.lab_sample_id, adminViewEmail]);
  const returnToReviewQueue = useCallback(() => navigate("/admin?section=reports"), [navigate]);
  const handleApproved = useCallback((result) => {
    setApprovalNotice(result?.confirmationMessage || "The report approval was saved successfully.");
    const next = result?.nextReport;
    if (next?.labSampleId) {
      navigate(`/dashboard?adminEmail=${encodeURIComponent(next.customerEmail||"")}&sampleId=${encodeURIComponent(next.labSampleId)}&review=1`,{replace:true});
    } else {
      window.setTimeout(() => navigate("/admin?section=reports",{replace:true}), 2500);
    }
  }, [navigate]);

  useEffect(() => {
    if (!approvalNotice) return undefined;
    const timer = window.setTimeout(() => setApprovalNotice(""), 7000);
    return () => window.clearTimeout(timer);
  }, [approvalNotice]);
  const customerInfo = useMemo(
    () => buildCustomerInfo({ samples, user, adminViewEmail }),
    [samples, user, adminViewEmail]
  );

  useEffect(() => {
    if (vehicleGroups.length === 0) {
      setSelectedVehicleId(null);
      setSelectedId(null);
      return;
    }

    const shouldApplyAdminDeepLink = adminReportId && appliedAdminDeepLinkKey !== adminDeepLinkKey;
    const reportGroup = shouldApplyAdminDeepLink
      ? vehicleGroups.find(group => group.samples.some(sample => sample.lab_sample_id === adminReportId))
      : null;
    if (reportGroup && selectedVehicleId !== reportGroup.id) {
      setSelectedVehicleId(reportGroup.id);
      return;
    }
    if (reportGroup && !reportGroup.readySamples.some(sample => sample.lab_sample_id === adminReportId)) {
      setAppliedAdminDeepLinkKey(adminDeepLinkKey);
    }

    const currentGroup = vehicleGroups.find(group => group.id === selectedVehicleId);
    if (currentGroup) return;

    const firstReadyGroup = vehicleGroups.find(group => group.latestReadySample) || vehicleGroups[0];
    setSelectedVehicleId(firstReadyGroup.id);
  }, [vehicleGroups, selectedVehicleId, adminReportId, adminDeepLinkKey, appliedAdminDeepLinkKey]);

  useEffect(() => {
    if (!selectedGroup) {
      setSelectedId(null);
      return;
    }

    const shouldApplyAdminDeepLink = adminReportId && appliedAdminDeepLinkKey !== adminDeepLinkKey;
    if (shouldApplyAdminDeepLink && selectedGroup.readySamples.some(sample => sample.lab_sample_id === adminReportId)) {
      if (selectedId === adminReportId) {
        setAppliedAdminDeepLinkKey(adminDeepLinkKey);
        return;
      }
      setSelectedId(adminReportId);
      setAppliedAdminDeepLinkKey(adminDeepLinkKey);
      return;
    }

    const currentReportStillAvailable = selectedGroup.readySamples.some(sample => sample.lab_sample_id === selectedId);
    if (currentReportStillAvailable) return;

    setSelectedId(selectedGroup.latestReadySample?.lab_sample_id || null);
  }, [selectedGroup, selectedId, adminReportId, adminDeepLinkKey, appliedAdminDeepLinkKey]);

  const openAlphaFeedback = useCallback(() => setShowAlphaFeedback(true), []);
  const closeAlphaFeedback = useCallback(() => setShowAlphaFeedback(false), []);

  const alphaFeedbackLauncher = isAlphaTester ? (
    <AlphaFeedbackLauncher
      selectedSample={selectedSample}
      isOpen={showAlphaFeedback}
      onOpen={openAlphaFeedback}
      onClose={closeAlphaFeedback}
    />
  ) : null;

  if (loading) {
    return (
      <div style={{ ...s.page, padding: isMobile ? "16px 12px" : "var(--dashboard-page-pad-top) 24px" }}>
        <div style={s.loadingBox}>
          <div style={s.loadingCard} className="animate-fade-in-up">
            <div style={{ display: "flex", justifyContent: "center" }}>
              <div style={s.spinner} aria-hidden="true"></div>
            </div>
            <div style={s.loadingTitle}>Loading Dashboard</div>
            <div style={s.loadingText}>
              We are pulling the latest report, vehicle, and threshold data. This can take a moment for larger accounts.
            </div>
            <div style={s.loadingTrack} role="progressbar" aria-label="Dashboard loading">
              <div style={s.loadingFill} className="sdx-dashboard-loading-bar"></div>
            </div>
            <div style={s.loadingMeta}>Still working...</div>
          </div>
        </div>
        {alphaFeedbackLauncher}
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...s.page, padding: isMobile ? "16px 12px" : "var(--dashboard-page-pad-top) 24px" }}>
        <div style={s.errorCard}>
          <div style={s.errorText}>{error}</div>
          <button style={s.retryBtn} onClick={fetchSamples}>Retry</button>
        </div>
        {alphaFeedbackLauncher}
      </div>
    );
  }

  if (samples.length === 0) {
    return (
      <div style={{ ...s.page, padding: isMobile ? "16px 12px" : "var(--dashboard-page-pad-top) 24px" }}>
        <AccountHeader customerInfo={customerInfo} user={user} adminViewEmail={adminViewEmail} onOpenInfo={() => setShowMyInfo(true)} />
        <div style={s.pendingCard} className="animate-fade-in-up">
          <div style={s.pendingIcon}>&#128269;</div>
          <div style={s.pendingTitle}>No samples found</div>
          <div style={s.pendingText}>
            We couldn't find any oil analysis samples associated with your account.
            If you've recently submitted a sample, please allow some time for it to be registered.
          </div>
        </div>
        {showMyInfo && <CustomerInfoModal customerInfo={customerInfo} onClose={() => setShowMyInfo(false)} />}
        {alphaFeedbackLauncher}
      </div>
    );
  }

  if (allPending) {
    return (
      <div style={{ ...s.page, padding: isMobile ? "16px 12px" : "var(--dashboard-page-pad-top) 24px" }}>
        <AccountHeader customerInfo={customerInfo} user={user} adminViewEmail={adminViewEmail} onOpenInfo={() => setShowMyInfo(true)} />
        <div style={s.pendingCard} className="animate-fade-in-up">
          <div style={s.pendingIcon}>&#9203;</div>
          <div style={s.pendingTitle}>Your sample is being processed</div>
          <div style={s.pendingText}>
            We've received your oil sample{samples.length > 1 ? "s" : ""} and
            {samples.length > 1 ? " they are" : " it is"} currently being analyzed
            in our laboratory. We'll have your results ready soon.
          </div>
          <div style={{ marginTop: 20, fontSize: 13, color: "var(--text-faint)" }}>
            {samples.length} sample{samples.length > 1 ? "s" : ""} pending
          </div>
        </div>
        {showMyInfo && <CustomerInfoModal customerInfo={customerInfo} onClose={() => setShowMyInfo(false)} />}
        {alphaFeedbackLauncher}
      </div>
    );
  }

  return (
    <div style={{ ...s.page, padding: reviewMode ? (isMobile ? "196px 12px 16px" : "124px 24px 32px") : (isMobile ? "16px 12px" : "var(--dashboard-page-pad-top) 24px") }}>
      {approvalNotice && <div role="status" aria-live="polite" style={s.approvalNotice}>{approvalNotice}</div>}
      <div className="animate-fade-in-up">
        <AccountHeader customerInfo={customerInfo} user={user} adminViewEmail={adminViewEmail} onOpenInfo={() => setShowMyInfo(true)} />

        <ReportSelector
          vehicleGroups={vehicleGroups}
          selectedVehicleId={selectedVehicleId}
          onVehicleSelect={setSelectedVehicleId}
        />

        {selectedGroup && !selectedSample && (
          <div className="animate-fade-in" data-report-learning-capture-root="true">
            <ReportHistorySelector
              selectedGroup={selectedGroup}
              selectedReportId={selectedId}
              onReportSelect={setSelectedId}
              feedbackDraftReportIds={feedbackDraftReportIds}
            />
            <div style={{ ...s.pendingCard, margin: "24px auto" }}>
              <div style={s.pendingIcon}>&#9203;</div>
              <div style={s.pendingTitle}>Reports are being processed</div>
              <div style={s.pendingText}>
                This vehicle only has pending oil reports right now. Results will appear here once testing is complete.
              </div>
            </div>
          </div>
        )}

        {selectedSample && (
          <div className="animate-fade-in">
            <div data-report-learning-capture-root="true">
              <SampleSummary
                sample={selectedSample}
                onOpenSettings={isAdminRole ? () => setShowSettings(true) : undefined}
                detailsExpanded={vehicleDetailsExpanded}
                onDetailsExpandedChange={setVehicleDetailsExpanded}
              />
              {isAdminRole && !reviewMode && (
                <AdminThresholdTemplateSection
                  templateName={selectedTemplateName}
                  thresholdMode={selectedDetail?.thresholdMode}
                  onOpenReview={() => setShowReportReview(true)}
                />
              )}
              <VehicleTrendHero
                vehicleGroup={selectedGroup}
                selectedSample={selectedSample}
                selectedThresholds={activeReportLearningPreview?.thresholds || currentSelectedDetail?.thresholds || null}
                activeCategory={activeGaugeTab}
                onCategorySelect={setActiveGaugeTab}
                reportSelector={(
                  <ReportHistorySelector
                    selectedGroup={selectedGroup}
                    selectedReportId={selectedId}
                    onReportSelect={setSelectedId}
                    feedbackDraftReportIds={feedbackDraftReportIds}
                    compact
                  />
                )}
              />
              <GaugeGrid
                sample={selectedSample}
                reportHistory={selectedGroup.readySamples}
                detailEmail={adminViewEmail}
                activeTab={activeGaugeTab}
                onTabChange={setActiveGaugeTab}
                onTemplateResolved={handleTemplateResolved}
                onDetailLoaded={handleDetailLoaded}
                refreshKey={detailRefreshKey}
                detailOverride={reviewMode ? activeReportLearningPreview : null}
                onMetricAction={reviewMode && activeReportLearningPreview?.metricEditingEnabled && activeReportLearningPreview?.comparisonView !== "learning" ? (metricKey) => setReviewMetricRequest({ metricKey, requestedAt: Date.now() }) : undefined}
              />
              {!reviewMode && <OilGeekNote note={selectedDetail?.oilGeekNote} />}
            </div>
            {reviewMode && (
              <ReportReviewModal
                key={selectedSample.lab_sample_id}
                sampleId={selectedSample.lab_sample_id}
                detail={{...(currentSelectedDetail || {}),sample:selectedSample,templateName:currentSelectedDetail ? selectedTemplateName : ""}}
                customerEmail={adminViewEmail}
                embedded
                dashboardReview
                onSaved={refreshSelectedDetail}
                onApproved={handleApproved}
                onReturn={returnToReviewQueue}
                onLearningPreview={handleReportLearningPreview}
                feedbackDraftMemory={feedbackDraftMemoryRef.current}
                onFeedbackDraftStateChange={handleFeedbackDraftStateChange}
                metricRequest={reviewMetricRequest}
                onMetricRequestHandled={() => setReviewMetricRequest(null)}
              />
            )}
          </div>
        )}

        {isAdminRole && showSettings && (
          <VehicleSettingsModal
            samples={samples}
            vehicleGroups={vehicleGroups}
            selectedGroup={selectedGroup}
            selectedSample={selectedSample}
            customerEmail={adminViewEmail}
            onChanged={fetchSamples}
            onClose={() => setShowSettings(false)}
          />
        )}

        {showMyInfo && (
          <CustomerInfoModal
            customerInfo={customerInfo}
            onClose={() => setShowMyInfo(false)}
          />
        )}

        {alphaFeedbackLauncher}

        {showReportReview && selectedSample && (
          <ReportReviewModal
            sampleId={selectedSample.lab_sample_id}
            detail={{
              ...selectedDetail,
              sample: selectedSample,
              templateName: selectedTemplateName,
            }}
            customerEmail={adminViewEmail}
            onSaved={refreshSelectedDetail}
            onClose={() => setShowReportReview(false)}
          />
        )}
      </div>
    </div>
  );
}

function CustomerInfoModal({ customerInfo, onClose }) {
  const fields = [
    ["Name", customerInfo.name],
    ["Email", customerInfo.email],
    ["Phone", customerInfo.phone],
    ["Address", customerInfo.address],
  ];

  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section style={{ ...s.modal, width: "min(680px, 96vw)" }} onMouseDown={(event) => event.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>My Info</div>
            <div style={s.modalHint}>Customer information associated with this dashboard account.</div>
          </div>
          <button type="button" style={s.modalClose} onClick={onClose}>Close</button>
        </div>
        <div style={s.infoGrid}>
          {fields.map(([label, value]) => (
            <div key={label} style={s.infoCard}>
              <div style={s.infoLabel}>{label}</div>
              <div style={{ ...s.infoValue, whiteSpace: label === "Address" ? "pre-line" : "normal" }}>
                {value || "Not available"}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function AlphaFeedbackLauncher({ selectedSample, isOpen, onOpen, onClose }) {
  const [feedbackType, setFeedbackType] = useState("question");
  const [message, setMessage] = useState("");
  const [includeReport, setIncludeReport] = useState(Boolean(selectedSample?.lab_sample_id));
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const launcherRef = useRef(null);
  const closeButtonRef = useRef(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) setIncludeReport(Boolean(selectedSample?.lab_sample_id));
  }, [isOpen, selectedSample?.lab_sample_id]);

  useEffect(() => {
    if (isOpen) {
      wasOpenRef.current = true;
      window.requestAnimationFrame(() => closeButtonRef.current?.focus());
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      window.requestAnimationFrame(() => launcherRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    const cleaned = message.trim();
    if (cleaned.length < 5) {
      setError("Please enter at least 5 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitAlphaFeedback({
        feedbackType,
        message: cleaned,
        labSampleId: includeReport ? selectedSample?.lab_sample_id || null : null,
      });
      setNotice(result.data?.message || "Thanks—your feedback was sent to the SPEEDiagnostix team.");
      setMessage("");
    } catch (err) {
      setError(err.response?.data?.error || "Your feedback could not be sent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!isOpen ? (
        <button
          type="button"
          ref={launcherRef}
          className="sdx-alpha-feedback-fab"
          onClick={onOpen}
          aria-expanded="false"
          aria-controls="sdx-alpha-feedback-drawer"
        >
          <span className="sdx-alpha-feedback-fab-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none">
              <path d="M5 5.5h14v10H9l-4 3v-13Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M8 9h8M8 12h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </span>
          <span className="sdx-alpha-feedback-fab-copy">
            <strong>Share Alpha Feedback</strong>
            <small>Help improve your dashboard</small>
          </span>
        </button>
      ) : (
        <div className="sdx-alpha-feedback-backdrop" onMouseDown={onClose}>
          <aside
            id="sdx-alpha-feedback-drawer"
            className="sdx-alpha-feedback-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sdx-alpha-feedback-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="sdx-alpha-feedback-drawer-accent" aria-hidden="true"></div>
            <div style={s.modalHeader}>
              <div>
                <div id="sdx-alpha-feedback-title" style={s.modalTitle}>Shape the SPEEDiagnostix Alpha</div>
                <div style={{ ...s.modalHint, fontSize: 13, lineHeight: 1.5 }}>Ask a question, report a problem, or suggest an improvement. Lake will receive it in Admin Alerts.</div>
              </div>
              <button ref={closeButtonRef} type="button" className="sdx-alpha-feedback-close" onClick={onClose} aria-label="Close alpha feedback drawer">&times;</button>
            </div>
            <div className="sdx-alpha-feedback-prompt">
              <strong>Your experience matters.</strong>
              Tell us what feels useful, confusing, missing, or broken.
            </div>
            <form onSubmit={handleSubmit}>
              <label style={{ display: "block", color: "var(--text-primary)", fontSize: 13, fontWeight: 800, marginBottom: 12 }}>
                Feedback type
                <select style={{ ...s.modalClose, display: "block", width: "100%", marginTop: 6 }} value={feedbackType} onChange={(event) => setFeedbackType(event.target.value)}>
                  <option value="question">Question</option>
                  <option value="problem">Problem</option>
                  <option value="suggestion">Suggestion</option>
                </select>
              </label>
              {selectedSample?.lab_sample_id && (
                <label style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-primary)", fontSize: 13, marginBottom: 12 }}>
                  <input type="checkbox" checked={includeReport} onChange={(event) => setIncludeReport(event.target.checked)} />
                  Attach report {selectedSample.lab_sample_id} to this feedback
                </label>
              )}
              <label style={{ display: "block", color: "var(--text-primary)", fontSize: 13, fontWeight: 800 }}>
                Message
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  maxLength={2000}
                  rows={7}
                  placeholder="What would you like Lake to know or answer?"
                  style={{ width: "100%", boxSizing: "border-box", marginTop: 6, padding: 12, borderRadius: 8, border: "1.5px solid var(--border)", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", resize: "vertical", font: "inherit" }}
                />
              </label>
              <div style={{ ...s.modalHint, textAlign: "right", marginTop: 4 }}>{message.length}/2000</div>
              {error && <div role="alert" style={{ marginTop: 10, color: "#b91c1c", fontWeight: 800 }}>{error}</div>}
              {notice && <div role="status" style={{ marginTop: 10, color: "#15803d", fontWeight: 800 }}>{notice}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button type="button" style={s.modalClose} onClick={onClose}>Cancel</button>
                <button type="submit" className="sdx-alpha-feedback-submit" disabled={submitting || message.trim().length < 5}>
                  {submitting ? "Sending…" : "Send to Lake"}
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}

function VehicleSettingsModal({ samples, vehicleGroups, selectedGroup, selectedSample, customerEmail, onChanged, onClose }) {
  return (
    <div style={s.modalBackdrop} onMouseDown={onClose}>
      <section style={s.modal} onMouseDown={(event) => event.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <div style={s.modalTitle}>Vehicle/Report Settings</div>
            <div style={s.modalHint}>Manage vehicle names, VINs, report placement, vehicle merges, and account/report review requests.</div>
          </div>
          <button type="button" style={s.modalClose} onClick={onClose}>Close</button>
        </div>
        <VehicleCorrectionActions
          samples={samples}
          vehicleGroups={vehicleGroups}
          selectedGroup={selectedGroup}
          selectedSample={selectedSample}
          customerEmail={customerEmail}
          onChanged={onChanged}
          embedded
        />
      </section>
    </div>
  );
}
